import { useEffect, useRef, useState } from "react";
import { Filter, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { cn } from "@/lib/cn.js";
import type { AppointmentState, Customer, Resource, Service } from "@/lib/types.js";
import { ALL_LIFECYCLE_STATES, DEFAULT_VISIBLE_STATES } from "./calendar-filters.js";
import type { CalendarView } from "./use-calendar-url.js";

/**
 * PRD calendar-ux-parity REQ-CUX-009 + the toolbar diagram. Hosts:
 *  - View switcher (Day | Week | Month | Resource), each button carries a
 *    `data-view-active` attribute so tests can deterministically assert
 *    selection regardless of CSS class churn.
 *  - Search input (REQ-CUX-014).
 *  - Customer filter (single-select typeahead, REQ-CUX-015).
 *  - Service filter (multi-select).
 *  - Resource filter (multi-select).
 *  - Lifecycle-state chip group with default = REQ-CUX-011 (hide cancelled +
 *    no_show).
 *  - "Clear filters" affordance when any filter is active.
 */

export interface CalendarToolbarProps {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  /** Currently selected resource for week/resource view (single select). */
  selectedResourceId: string | undefined;
  onSelectedResourceIdChange: (id: string) => void;
  resources: Resource[];
  services: Service[];
  customers: Customer[];
  filters: {
    customer: string | undefined;
    services: string[];
    resources: string[];
    states: string[];
    search: string;
  };
  onCustomerChange: (id: string | undefined) => void;
  onServicesChange: (ids: string[]) => void;
  onResourcesChange: (ids: string[]) => void;
  onStatesChange: (states: string[]) => void;
  onSearchChange: (q: string) => void;
  onClearFilters: () => void;
}

const VIEW_BUTTONS: Array<{ value: CalendarView; label: string }> = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "resource", label: "Resource" }
];

export function CalendarToolbar({
  view,
  onViewChange,
  selectedResourceId,
  onSelectedResourceIdChange,
  resources,
  services,
  customers,
  filters,
  onCustomerChange,
  onServicesChange,
  onResourcesChange,
  onStatesChange,
  onSearchChange,
  onClearFilters
}: CalendarToolbarProps): React.JSX.Element {
  // The states the chip group treats as "active". Empty filters list →
  // DEFAULT_VISIBLE_STATES are visually selected (REQ-CUX-011).
  const activeStates = new Set(
    filters.states.length > 0 ? filters.states : DEFAULT_VISIBLE_STATES
  );

  const toggleState = (state: AppointmentState): void => {
    const baseSet = new Set<string>(
      filters.states.length > 0 ? filters.states : DEFAULT_VISIBLE_STATES
    );
    if (baseSet.has(state)) {
      baseSet.delete(state);
    } else {
      baseSet.add(state);
    }
    onStatesChange(Array.from(baseSet));
  };

  // Compact mobile-only count badge for the "Filters" trigger.
  const activeFilterCount =
    (filters.customer ? 1 : 0) +
    filters.services.length +
    filters.resources.length +
    (filters.states.length > 0 ? 1 : 0);

  return (
    <div className="flex flex-col gap-2">
      {/* Row 1: view switcher */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border bg-card">
          {VIEW_BUTTONS.map((btn) => {
            const active = view === btn.value;
            return (
              <button
                type="button"
                key={btn.value}
                onClick={() => onViewChange(btn.value)}
                data-view-active={active ? "true" : "false"}
                aria-label={`${btn.label} view`}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium transition-colors duration-100 motion-reduce:transition-none",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {btn.label}
              </button>
            );
          })}
        </div>
        {(view === "week" || view === "resource") && (
          <select
            value={selectedResourceId ?? ""}
            onChange={(e) => onSelectedResourceIdChange(e.target.value)}
            data-testid="resource-select"
            className="h-8 rounded-md border bg-card px-2 text-xs"
            aria-label="Selected resource"
          >
            {resources
              .filter((r) => !r.deleted_at)
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
          </select>
        )}
      </div>
      {/* Row 2: search + filters. On mobile (<lg) the dropdown filters and
          state chips collapse behind a single "Filters (n)" sheet so the
          toolbar stays one row tall — search remains primary. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-auto">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, notes…"
            value={filters.search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-8 sm:w-56"
            aria-label="Search appointments"
          />
        </div>

        {/* Inline filters — desktop+ only. */}
        <div className="hidden flex-wrap items-center gap-2 lg:flex">
          <CustomerFilter
            customers={customers}
            value={filters.customer}
            onChange={onCustomerChange}
          />
          <MultiSelectFilter
            label="Service"
            items={services
              .filter((s) => !s.deleted_at)
              .map((s) => ({ id: s.id, name: s.name }))}
            values={filters.services}
            onChange={onServicesChange}
          />
          <MultiSelectFilter
            label="Resource"
            items={resources
              .filter((r) => !r.deleted_at)
              .map((r) => ({ id: r.id, name: r.name }))}
            values={filters.resources}
            onChange={onResourcesChange}
          />
          <div className="flex items-center gap-1">
            {ALL_LIFECYCLE_STATES.map((state) => {
              const active = activeStates.has(state);
              return (
                <button
                  type="button"
                  key={state}
                  data-testid={`state-chip-${state}`}
                  data-active={active ? "true" : "false"}
                  onClick={() => toggleState(state)}
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider transition-colors duration-100 motion-reduce:transition-none",
                    active
                      ? "bg-primary/15 text-primary"
                      : "bg-muted text-muted-foreground hover:bg-muted/70"
                  )}
                >
                  {state.replace(/_/g, " ")}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile-only "Filters (n)" sheet. */}
        <div className="lg:hidden">
          <MobileFilterSheet
            customers={customers}
            services={services}
            resources={resources}
            filters={filters}
            activeStates={activeStates}
            activeCount={activeFilterCount}
            toggleState={toggleState}
            onCustomerChange={onCustomerChange}
            onServicesChange={onServicesChange}
            onResourcesChange={onResourcesChange}
          />
        </div>

        {hasAnyFilter(filters) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            data-testid="clear-filters"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Mobile sheet that bundles every dropdown filter + the lifecycle chips into
 * one trigger. The sheet body re-uses the same primitives so behavior is
 * identical to desktop — only the layout differs.
 */
function MobileFilterSheet({
  customers,
  services,
  resources,
  filters,
  activeStates,
  activeCount,
  toggleState,
  onCustomerChange,
  onServicesChange,
  onResourcesChange
}: {
  customers: Customer[];
  services: Service[];
  resources: Resource[];
  filters: CalendarToolbarProps["filters"];
  activeStates: Set<string>;
  activeCount: number;
  toggleState: (state: AppointmentState) => void;
  onCustomerChange: (id: string | undefined) => void;
  onServicesChange: (ids: string[]) => void;
  onResourcesChange: (ids: string[]) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-md border border-border-strong bg-card px-3 text-xs font-medium text-foreground shadow-xs",
          activeCount > 0 && "border-primary/40 bg-primary/5 text-primary"
        )}
      >
        <Filter className="h-3.5 w-3.5" />
        Filters{activeCount > 0 && ` (${activeCount})`}
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-40 flex items-end"
        >
          <div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="relative z-10 w-full max-h-[85dvh] overflow-y-auto rounded-t-xl border-t bg-card p-4 shadow-lg animate-scale-in">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Filters</h3>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
            <div className="flex flex-col gap-3">
              <CustomerFilter
                customers={customers}
                value={filters.customer}
                onChange={onCustomerChange}
              />
              <MultiSelectFilter
                label="Service"
                items={services
                  .filter((s) => !s.deleted_at)
                  .map((s) => ({ id: s.id, name: s.name }))}
                values={filters.services}
                onChange={onServicesChange}
              />
              <MultiSelectFilter
                label="Resource"
                items={resources
                  .filter((r) => !r.deleted_at)
                  .map((r) => ({ id: r.id, name: r.name }))}
                values={filters.resources}
                onChange={onResourcesChange}
              />
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  States
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_LIFECYCLE_STATES.map((state) => {
                    const active = activeStates.has(state);
                    return (
                      <button
                        type="button"
                        key={state}
                        data-testid={`state-chip-mobile-${state}`}
                        data-active={active ? "true" : "false"}
                        onClick={() => toggleState(state)}
                        className={cn(
                          "rounded-full px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider",
                          active
                            ? "bg-primary/15 text-primary"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {state.replace(/_/g, " ")}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function hasAnyFilter(filters: CalendarToolbarProps["filters"]): boolean {
  if (filters.customer) return true;
  if (filters.services.length > 0) return true;
  if (filters.resources.length > 0) return true;
  if (filters.states.length > 0) return true;
  if (filters.search.length > 0) return true;
  return false;
}

/**
 * Single-select customer typeahead (REQ-CUX-015). Matches against
 * `display_name` and `display_phone_e164` (post customer-refs cutover) which,
 * at the data layer the calendar consumes (`Customer`), correspond to
 * `name` and `phone_e164`. The dropdown lists all customers; a filter input
 * narrows the visible set as the user types.
 */
function CustomerFilter({
  customers,
  value,
  onChange
}: {
  customers: Customer[];
  value: string | undefined;
  onChange: (id: string | undefined) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = customers.find((c) => c.id === value);
  const filterLower = filter.toLowerCase();
  const filtered = customers.filter((c) => {
    if (c.deleted_at) return false;
    if (!filterLower) return true;
    return (
      c.name.toLowerCase().includes(filterLower) ||
      (c.phone_e164 ?? "").includes(filter)
    );
  });

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((p) => !p)}
        className={cn(
          "inline-flex items-center gap-1 rounded-md border border-border-strong bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-xs hover:bg-accent",
          selected && "border-primary/40 bg-primary/5"
        )}
      >
        Customer{selected ? `: ${selected.name}` : ""}
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-30 mt-1.5 w-64 rounded-md border bg-popover p-1 shadow-md"
        >
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter customers…"
            className="mb-1 h-8 text-xs"
            autoFocus
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
              className="w-full rounded-sm px-2 py-1 text-left text-xs text-muted-foreground hover:bg-accent"
            >
              Clear customer
            </button>
          )}
          <div className="max-h-56 overflow-auto">
            {filtered.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No matches</div>
            ) : (
              filtered.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  role="option"
                  aria-selected={c.id === value}
                  onClick={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                  className={cn(
                    "block w-full truncate rounded-sm px-2 py-1 text-left text-xs hover:bg-accent",
                    c.id === value && "bg-primary/10 text-primary"
                  )}
                >
                  <span className="font-medium">{c.name}</span>
                  {c.phone_e164 && (
                    <span className="ml-1 text-muted-foreground">{c.phone_e164}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select chip-style filter. Used for Service and Resource filters
 * (REQ-CUX-009). Selecting items toggles their inclusion; the button label
 * shows the count of active selections.
 */
function MultiSelectFilter({
  label,
  items,
  values,
  onChange
}: {
  label: string;
  items: Array<{ id: string; name: string }>;
  values: string[];
  onChange: (next: string[]) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (id: string): void => {
    const set = new Set(values);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange(Array.from(set));
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((p) => !p)}
        className={cn(
          "inline-flex items-center gap-1 rounded-md border border-border-strong bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-xs hover:bg-accent",
          values.length > 0 && "border-primary/40 bg-primary/5"
        )}
      >
        {label}
        {values.length > 0 && ` (${values.length})`}
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-30 mt-1.5 w-56 rounded-md border bg-popover p-1 shadow-md"
        >
          <div className="max-h-56 overflow-auto">
            {items.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No options</div>
            ) : (
              items.map((it) => {
                const active = values.includes(it.id);
                return (
                  <button
                    type="button"
                    key={it.id}
                    role="option"
                    aria-selected={active}
                    onClick={() => toggle(it.id)}
                    className={cn(
                      "block w-full truncate rounded-sm px-2 py-1 text-left text-xs hover:bg-accent",
                      active && "bg-primary/10 text-primary"
                    )}
                  >
                    {active ? "✓ " : "  "}
                    {it.name}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
